import jsPDF from "jspdf";
import { MONTHS, diffDays } from "@/lib/calendarUtils";

const colorRGB = (b, isPast) => {
  if (isPast) return [148, 163, 184];
  if (b.status === "opzionata") return [56, 189, 248];
  const ps = b.payment_status || "none";
  if (ps === "full") return [16, 185, 129];
  if (ps === "partial") return [251, 191, 36];
  return [249, 115, 22];
};

const WD = ["D", "L", "M", "M", "G", "V", "S"];
const wd = (s) => WD[new Date(s + "T00:00:00Z").getUTCDay()];
const isWeekend = (s) => { const d = new Date(s + "T00:00:00Z").getUTCDay(); return d === 0 || d === 6; };

function loadLogo(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawMonth(doc, year, month, properties, bars, logo, structureName) {
  const pageW = 297, pageH = 210;
  const mL = 8, mR = 8, mT = 10;
  const numDays = new Date(year, month + 1, 0).getDate();
  const mm = String(month + 1).padStart(2, "0");
  const monthStart = `${year}-${mm}-01`;
  const days = Array.from({ length: numDays }, (_, i) => `${year}-${mm}-${String(i + 1).padStart(2, "0")}`);
  const label = `${MONTHS[month]} ${year}`;

  if (logo) doc.addImage(logo, "PNG", mL, mT - 6, 10, 10);
  const titleX = mL + (logo ? 13 : 0);
  doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.setTextColor(15, 23, 42);
  doc.text(`Calendario prenotazioni — ${label}${structureName ? " · " + structureName : ""}`, titleX, mT);
  doc.setFontSize(8); doc.setFont(undefined, "normal"); doc.setTextColor(100, 116, 139);
  doc.text("Blu Lodge Maldives", pageW - mR, mT, { align: "right" });

  const rooms = properties || [];
  const leftW = 44;
  const gridX = mL + leftW;
  const gridTop = mT + 5;
  const headerH = 7;
  const availW = pageW - mR - gridX;
  const dayW = availW / numDays;
  const bodyTop = gridTop + headerH;
  const roomStruct = {}; const structOrder = []; const structNameMap = {};
  rooms.forEach((p) => { roomStruct[p.id] = p.structure_id; if (!(p.structure_id in structNameMap)) { structNameMap[p.structure_id] = p.structure_name || "Struttura"; structOrder.push(p.structure_id); } });
  const multiStruct = structOrder.length > 1;
  const presRows = multiStruct ? [...structOrder, "__total__"] : [null];  // per struttura + Totale (multi) oppure "Presenze" (singola)
  const sh = 3.3;
  const legendH = 8;
  const summaryH = (presRows.length + 2) * sh + 2;
  const gridBottom = pageH - 5 - legendH - summaryH;
  const rowH = Math.min(7.5, Math.max(3.2, (gridBottom - bodyTop) / Math.max(1, rooms.length)));
  const bodyH = rowH * rooms.length;
  const today = new Date().toISOString().slice(0, 10);

  for (let i = 0; i < numDays; i++) {
    if (isWeekend(days[i])) { doc.setFillColor(241, 245, 249); doc.rect(gridX + i * dayW, bodyTop, dayW, bodyH, "F"); }
  }
  doc.setFontSize(6); doc.setTextColor(71, 85, 105);
  for (let i = 0; i < numDays; i++) {
    const x = gridX + i * dayW + dayW / 2;
    doc.text(String(i + 1), x, gridTop + 3, { align: "center" });
    doc.text(wd(days[i]), x, gridTop + 6, { align: "center" });
  }
  doc.setDrawColor(230, 236, 242); doc.setLineWidth(0.1);
  for (let i = 0; i <= numDays; i++) { const x = gridX + i * dayW; doc.line(x, bodyTop, x, bodyTop + bodyH); }

  let prevStruct = null;
  rooms.forEach((room, r) => {
    const y = bodyTop + r * rowH;
    doc.setDrawColor(230, 236, 242); doc.line(mL, y, gridX + availW, y);
    const sname = room.structure_name || "";
    if (sname !== prevStruct) { doc.setFont(undefined, "bold"); prevStruct = sname; } else { doc.setFont(undefined, "normal"); }
    doc.setTextColor(30, 41, 59); doc.setFontSize(6);
    const nm = (sname ? sname.slice(0, 8) + " · " : "") + (room.name || "");
    doc.text(nm.slice(0, 34), mL + 1, y + rowH / 2 + 1.4);
    bars.filter((x) => x.property_id === room.id).forEach((x) => {
      const off = diffDays(monthStart, x.checkin);
      const nights = Math.max(1, diffDays(x.checkin, x.checkout));
      let sx = (off + 0.5) * dayW;
      let ex = (off + nights + 0.5) * dayW;
      const maxX = numDays * dayW;
      if (ex <= 0 || sx >= maxX) return;
      sx = Math.max(0, sx); ex = Math.min(maxX, ex);
      const isPast = (x.booking.checkout || "") < today;
      const [cr, cg, cb] = colorRGB(x.booking, isPast);
      const bx = gridX + sx, bw = Math.max(1.5, ex - sx - 0.4), bh = Math.max(2, rowH - 1.6);
      doc.setFillColor(cr, cg, cb);
      doc.roundedRect(bx, y + 0.8, bw, bh, 0.5, 0.5, "F");
      const g = (x.booking.guest_surname || x.booking.guest_name || "").toString();
      if (bw > 9 && g && rowH >= 4) {
        doc.setTextColor(255, 255, 255); doc.setFontSize(5);
        doc.text(g.slice(0, Math.max(1, Math.floor(bw / 1.6))), bx + 1, y + rowH / 2 + 1.1);
      }
    });
  });
  doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.2);
  doc.line(mL, bodyTop + bodyH, gridX + availW, bodyTop + bodyH);
  doc.line(gridX, gridTop, gridX, bodyTop + bodyH);

  // Riepilogo giornaliero: Presenze (una riga per struttura se "Tutte") + Arrivi/Partenze
  const roomIds = new Set(rooms.map((p) => p.id));
  const sumTop = bodyTop + bodyH + 2;
  const nLines = presRows.length + 2;
  doc.setFillColor(248, 250, 252);
  doc.rect(mL, sumTop, leftW + availW, sh * nLines, "F");
  doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.1);
  for (let k = 0; k <= nLines; k++) doc.line(mL, sumTop + k * sh, gridX + availW, sumTop + k * sh);
  doc.line(gridX, sumTop, gridX, sumTop + sh * nLines);
  const arrIdx = presRows.length, parIdx = presRows.length + 1;
  for (let i = 0; i < numDays; i++) {
    let arr = 0, par = 0;
    const presByStruct = {};
    bars.forEach((x) => {
      if (!roomIds.has(x.property_id)) return;
      const b = x.booking; const pax = (b.adults || 0) + (b.children || 0);
      if (x.checkin === days[i]) arr += 1;
      if (x.checkout === days[i]) par += 1;
      if (x.checkin <= days[i] && days[i] < x.checkout) presByStruct[roomStruct[x.property_id]] = (presByStruct[roomStruct[x.property_id]] || 0) + pax;
    });
    const cx = gridX + i * dayW + dayW / 2;
    doc.setFontSize(5.5);
    presRows.forEach((sid, idx) => {
      const isTot = sid === null || sid === "__total__";
      const v = isTot ? Object.values(presByStruct).reduce((a, n) => a + n, 0) : (presByStruct[sid] || 0);
      doc.setFont(undefined, sid === "__total__" ? "bold" : "normal");
      doc.setTextColor(15, 23, 42); doc.text(v ? String(v) : "", cx, sumTop + idx * sh + 2.3, { align: "center" });
    });
    doc.setFont(undefined, "normal");
    doc.setTextColor(16, 122, 87); doc.text(arr ? "+" + arr : "", cx, sumTop + arrIdx * sh + 2.3, { align: "center" });
    doc.setTextColor(180, 83, 9); doc.text(par ? "-" + par : "", cx, sumTop + parIdx * sh + 2.3, { align: "center" });
  }
  doc.setFontSize(5.5); doc.setFont(undefined, "bold"); doc.setTextColor(51, 65, 85);
  presRows.forEach((sid, idx) => {
    const lbl = sid === null ? "Presenze" : sid === "__total__" ? "Totale" : (structNameMap[sid] || "").slice(0, 22);
    doc.text(lbl, mL + 1, sumTop + idx * sh + 2.3);
  });
  doc.text("Arrivi", mL + 1, sumTop + arrIdx * sh + 2.3);
  doc.text("Partenze", mL + 1, sumTop + parIdx * sh + 2.3);
  doc.setFont(undefined, "normal");

  const legendY = pageH - 5;
  const legend = [["Opzionata", [56, 189, 248]], ["Da incassare", [249, 115, 22]], ["Acconto", [251, 191, 36]], ["Saldato", [16, 185, 129]], ["Passata", [148, 163, 184]]];
  let lx = mL;
  doc.setFontSize(6);
  legend.forEach(([lbl, [r2, g2, b2]]) => {
    doc.setFillColor(r2, g2, b2); doc.rect(lx, legendY - 3, 3, 3, "F");
    doc.setTextColor(71, 85, 105); doc.text(lbl, lx + 4, legendY - 0.5);
    lx += 4 + doc.getTextWidth(lbl) + 6;
  });
}

// Riepilogo visivo delle prenotazioni (A4 orizzontale). months = numero di mesi consecutivi (1 pagina ciascuno).
// structureId opzionale: stampa solo le camere di quella guesthouse.
export async function exportCalendarMonthPdf({ year, month, properties, bars, months = 1, structureId = "", structureName = "" }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const logo = await loadLogo("/blu-lodge-logo.png");
  const rooms = structureId ? (properties || []).filter((p) => p.structure_id === structureId) : (properties || []);
  const n = Math.max(1, Number(months) || 1);
  for (let i = 0; i < n; i++) {
    const d = new Date(year, month + i, 1);
    if (i > 0) doc.addPage();
    drawMonth(doc, d.getFullYear(), d.getMonth(), rooms, bars, logo, structureId ? structureName : "");
  }
  const mm = String(month + 1).padStart(2, "0");
  const slug = structureName ? "_" + structureName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : "";
  const suffix = n > 1 ? `_${n}mesi` : "";
  doc.save(`calendario_${year}-${mm}${suffix}${slug}.pdf`);
}
